import { useState } from "react";
import {
  Button,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Compose24Regular,
  Chat24Regular,
} from "@fluentui/react-icons";
import { useAuth } from "@/auth/AuthContext";
import { CommandCenter } from "@/components/command-center/CommandCenter";
import { ComposePanel } from "@/components/compose/ComposePanel";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { Header } from "@/components/common/Header";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  contentNoOverflow: {
    flex: 1,
    overflow: "hidden",
  },
  fabRow: {
    position: "fixed" as const,
    bottom: "16px",
    right: "16px",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "center",
  },
  fab: {
    borderRadius: "50%",
    width: "48px",
    height: "48px",
    minWidth: "48px",
    padding: 0,
    background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
    color: "#fff",
    boxShadow: `0 4px 12px ${tokens.colorNeutralShadowAmbient}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  fabSecondary: {
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    minWidth: "40px",
    padding: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
    boxShadow: `0 2px 8px ${tokens.colorNeutralShadowAmbient}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

type View = "dashboard" | "compose" | "chat";

const VIEW_TITLES: Record<View, string> = {
  dashboard: "Home",
  compose: "AI Compose",
  chat: "AI Chat",
};

export function MainView() {
  const styles = useStyles();
  const { selectedCompany, logout } = useAuth();
  const [activeView, setActiveView] = useState<View>("dashboard");

  return (
    <div className={styles.root}>
      <Header
        companyName={selectedCompany?.name || "Bolsterup"}
        viewTitle={VIEW_TITLES[activeView]}
        onLogout={logout}
        onBack={activeView !== "dashboard" ? () => setActiveView("dashboard") : undefined}
      />

      <div className={activeView === "chat" ? styles.contentNoOverflow : styles.content}>
        {activeView === "dashboard" && <CommandCenter />}
        {activeView === "compose" && <ComposePanel />}
        {activeView === "chat" && <ChatPanel />}
      </div>

      {activeView === "dashboard" && (
        <div className={styles.fabRow}>
          <Button
            className={styles.fabSecondary}
            appearance="subtle"
            icon={<Chat24Regular />}
            onClick={() => setActiveView("chat")}
            title="AI Chat"
          />
          <Button
            className={styles.fab}
            appearance="primary"
            icon={<Compose24Regular />}
            onClick={() => setActiveView("compose")}
            title="AI Compose"
          />
        </div>
      )}
    </div>
  );
}
